import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import axiosInstance from '../../axiosConfig';

export const fetchMediaFolders = async (params) => {
  const response = await axiosInstance.get('/media_library/', {
    params,
  });
  return response.data;
};

// Le back attend `page_size` ; le front manipule `pageSize`.
export const buildMediaFoldersQueryParams = ({ pageSize, ...params } = {}) => {
  const out = {};
  Object.entries({ ...params, page_size: pageSize }).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') out[k] = v;
  });
  return out;
};

export const mediaFoldersQueryKey = (queryParams) => [
  'media-folders',
  queryParams,
];

export const mapMediaFolders = (folders) =>
  folders?.map((folder) => ({
    ...folder,
    key: folder.id || folder.path,
    type: folder.has_children ? 'folder' : 'leaf',
  })) || [];

export const useGetMediaFolders = (params = {}, options = {}) => {
  const queryParams = useMemo(
    () => buildMediaFoldersQueryParams(params),
    [
      params.path,
      params.search,
      params.order_by,
      params.media_type,
      params.already_sent_for_qualification,
      params.page,
      params.pageSize,
    ]
  );

  const { data, error, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: mediaFoldersQueryKey(queryParams),
    queryFn: () => fetchMediaFolders(queryParams),
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // Un dossier introuvable ne réapparaîtra pas : inutile de réessayer.
    retry: (failureCount, queryError) =>
      queryError?.response?.status !== 404 && failureCount < 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
  });

  const folders = useMemo(
    () => mapMediaFolders(data?.results?.folders),
    [data?.results?.folders]
  );

  const files = useMemo(
    () =>
      data?.results?.files?.map((file) => ({
        ...file,
        key: file.path,
      })) || [],
    [data?.results?.files]
  );

  const pagination = {
    total: data?.count || 0,
    current: params.page || 1,
    pageSize: params.pageSize || 10,
  };

  return {
    folders,
    files,
    pagination,
    error,
    isLoading,
    isError,
    isFetching,
    refetch,
  };
};
